import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useNotification } from "../context/NotificationContext";
import AuthService from "../services/AuthService";
import { useClient } from "../context/ClientContext";
import GooeySVG from "../shared/components/folderSVG";

interface FormData {
  email: string;
  password: string;
}

// A simple slider component for images in /assets/images/
const SimpleSlider = () => {
  // List out the images you want to display
  const images = [
    "/assets/images/slider1.png",
    "/assets/images/slider1.png",
    "/assets/images/slider1.png",
  ];

  const [currentIndex, setCurrentIndex] = useState(0);

  // Handlers for next/prev
  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <div className="relative w-full overflow-hidden">
      {/* Main image */}
      <img
        src={images[currentIndex]}
        alt="Slide"
        className="w-full h-fit items-center justify-center"
      />

      {/* Navigation buttons */}
      <div className="absolute bottom-2 left-0 right-0 flex justify-center space-x-2">
        <button
          onClick={handlePrev}
          className="px-3 py-1 bg-white text-gray-50 rounded shadow hover:bg-primary-200"
        >
          Prev
        </button>
        <button
          onClick={handleNext}
          className="px-3 py-1 bg-white text-gray-50 rounded shadow hover:bg-primary-200"
        >
          Next
        </button>
      </div>
    </div>
  );
};

const LoginPage = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>();
  const [loading, setLoading] = useState(false);
  const [hidePassword, setHidePassword] = useState(true);
  const navigate = useNavigate();
  const notification = useNotification();
  const client = useClient("AUTH_SERVICE");
  const authService = new AuthService(client, notification);

  const onSubmit = async (formData: FormData) => {
    setLoading(true);
    try {
      const response = await authService.login(formData);
      if (response.success) {
        notification.success(response.message);
        navigate("/");
      } else {
        notification.error("An error occurred during login.");
      }
    } catch (error: any) {
      notification.error(error.message || "An error occurred during login.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="min-h-full flex items-center justify-center relative">
      {/* Optional Gooey filter */}
      <GooeySVG />

      <div className="container mx-auto p-4 flex items-center justify-center">
        {/* We make two side-by-side "cards," each constrained by max-w-lg */}
        <div className="flex space-x-0 lg:space-x-4">
          {/* LEFT COLUMN - Form */}
          <div className="flex-none bg-white rounded-lg shadow-lg overflow-hidden max-w-lg p-8">
            <div className="text-center mb-8">
              <a href="https://www.serceaproject.org">
                <img
                  className="mx-auto w-24 mb-3"
                  src="/assets/images/sercea-2.png"
                  alt="Sercea Project Logo"
                  title="Sercea Project"
                />
              </a>

              <h4 className="text-xl font-extrabold text-primary-600 tracking-wider leading-tight">
                Strengthening Ethics and Responsible Conduct of Clinical Trial
                in East and Sub-Sahara Africa
              </h4>
            </div>

            {/* Login Form */}
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <p className="mb-4 text-base text-gray-700">
                Please login to your account
              </p>

              {/* Email Input */}
              <div className="mb-4">
                <label
                  htmlFor="email"
                  className="block text-sm font-semibold text-gray-700"
                >
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  placeholder="Enter valid email address"
                  className="mt-1 w-full border border-gray-300 rounded-md shadow-sm
                             focus:ring-primary-600 focus:border-primary-600 p-2"
                  {...register("email", { required: "Field is required" })}
                />
                {errors.email && (
                  <p className="text-red-600 text-xs mt-1">
                    {errors.email.message}
                  </p>
                )}
              </div>

              {/* Password Input */}
              <div className="mb-4">
                <label
                  htmlFor="password"
                  className="block text-sm font-semibold text-gray-700"
                >
                  Password
                </label>
                <div className="relative">
                  <input
                    type={hidePassword ? "password" : "text"}
                    placeholder="Please enter your password"
                    id="password"
                    className="mt-1 w-full border border-gray-300 rounded-md shadow-sm
                               focus:ring-primary-600 focus:border-primary-600 p-2"
                    {...register("password", {
                      required: "Field is required",
                    })}
                  />
                  <button
                    type="button"
                    onClick={() => setHidePassword(!hidePassword)}
                    className="absolute inset-y-0 right-0 flex items-center px-2 text-gray-600 hover:text-gray-800"
                  >
                    <span className="material-icons text-base">
                      {hidePassword ? "visibility_off" : "visibility"}
                    </span>
                  </button>
                </div>
                {errors.password && (
                  <p className="text-red-600 text-xs mt-1">
                    {errors.password.message}
                  </p>
                )}
              </div>

              {/* Submit button */}
              <div className="mb-4 pt-2 text-center">
                <button
                  type="submit"
                  disabled={loading}
                  className={`inline-block w-full rounded px-6 py-2 text-xs font-medium uppercase leading-normal text-white
                              shadow-md transition duration-150 ease-in-out
                              bg-primary-600 hover:bg-primary-700 
                              disabled:bg-gray-400 disabled:cursor-not-allowed`}
                >
                  {loading ? "Logging in..." : "Login"}
                </button>
              </div>

              {/* Forgot password link */}
              <div className="mb-6 text-center text-sm">
                Forgot Password
                <a
                  href="/forgot-password"
                  className="mx-1 text-sm text-secondary underline"
                >
                  Reset?
                </a>
              </div>

              {/* Register button */}
              <div className="flex items-center justify-center">
                <p className="mb-0 mr-2 text-sm text-gray-700">
                  Don't have an account?
                </p>
                <button
                  type="button"
                  onClick={() => navigate("/register")}
                  className={`inline-block rounded border-2 border-primary-600 px-4 py-1 text-xs font-medium uppercase leading-normal text-primary-600
                              hover:bg-primary-50 focus:outline-none 
                              transition duration-150 ease-in-out`}
                >
                  Register
                </button>
              </div>
            </form>
          </div>

          {/* RIGHT COLUMN - Slider, same width (max-w-lg) */}
          <div className="hidden lg:flex flex-none max-w-lg bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="w-full bg-gradient-to-r from-primary-600 via-pink-500 to-purple-500 p-6">
              <SimpleSlider />
              <div className="p-6 text-center bg-gray-100 w-full">
                <p className="text-sm text-gray-700 font-medium leading-relaxed">
                  <span className="font-semibold">
                    The Open Data Access Platform for Clinical Studies is a
                    web-based system designed to enhance data portability,
                    knowledge sharing, and evidence generation in clinical
                    research.
                  </span>
                  <span className="font-semibold">
                    {" "}
                    It provides researchers, policymakers, and institutions with
                    transparent and accessible data on clinical studies.
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LoginPage;
